import React, { useState, useEffect } from 'react';
import { Plus, Search, Trash2, User, CheckCircle, AlertCircle, MailCheck } from 'lucide-react';
import partnerService from '../services/partnerService';
import { userService } from '../services/userService';
import './UserSettings.css';
import './PartnerSettings.css';

const ROLES = [
  { value: 'PARTNER_ADMIN', label: 'Partner Admin' },
  { value: 'PARTNER_USER', label: 'Partner User' },
];

const PERMISSION_TO_ROLE = { A: 'PARTNER_ADMIN', U: 'PARTNER_USER', V: 'VENDOR' };
const ROLE_TO_PERMISSION = { PARTNER_ADMIN: 'A', PARTNER_USER: 'U', VENDOR: 'V' };

// A VENDOR IS NOT A COLLEAGUE, AND THE LIST SHOULD NOT PRETEND OTHERWISE.
//
// Thynk and dailypoint hold a permission row on the partner exactly as a person does — that is the whole
// point of modelling them as users — so they appear here. But a vendor is a machine integration: it has
// no email to invite, no password to reset, and its access comes from client credentials rather than a
// login. Left untagged it read as "Partner User", which invites somebody to resend an invitation to
// nobody, or to promote an integration to Partner Admin.
//
// So a vendor row is labelled, its role cannot be changed from here, and the invite action is withheld.
// Everything a vendor needs is on Partner Settings > Vendors.
const isVendor = (u) => u.permission_type === 'V';

const PartnerUsers = ({ partnerId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({ email: '', firstname: '', lastname: '', role: 'PARTNER_USER' });

  useEffect(() => { load(); }, [partnerId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await partnerService.getPartnerUsers(partnerId);
      setUsers(res.data?.users || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const flash = (text, type = 'success') => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleInvite = async () => {
    if (!form.email || !form.firstname || !form.lastname) {
      flash('Email, first name and last name are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await partnerService.invitePartnerUser(partnerId, form);
      flash('User invited successfully. A temporary password has been sent to their email.');
      setShowForm(false);
      setForm({ email: '', firstname: '', lastname: '', role: 'PARTNER_USER' });
      await load();
    } catch (err) {
      flash(err.response?.data?.error?.message || 'Failed to invite user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (userId) => {
    setSaving(true);
    try {
      await userService.reinviteUser(userId);
      flash('Invitation resent successfully');
    } catch (err) {
      flash(err.response?.data?.error?.message || 'Failed to resend invitation', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (u) => {
    setEditingUser({
      id: u.ID,
      email: u.loginname || '',
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      role: PERMISSION_TO_ROLE[u.permission_type] || 'PARTNER_USER',
      permission_id: u.permission_id,
    });
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await partnerService.updatePartnerUser(partnerId, editingUser.id, {
        firstname: editingUser.firstname,
        lastname: editingUser.lastname,
        role: editingUser.role,
      });
      flash('User updated successfully');
      setEditingUser(null);
      await load();
    } catch (err) {
      flash(err.response?.data?.error?.message || 'Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId, email) => {
    if (!window.confirm(`Remove ${email}? This will delete their account.`)) return;
    setSaving(true);
    try {
      await partnerService.removePartnerUser(partnerId, userId);
      setUsers(prev => prev.filter(u => u.ID !== userId));
      flash('User removed');
    } catch (err) {
      flash(err.response?.data?.error?.message || 'Failed to remove user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    (u.loginname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.firstname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.lastname || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="user-settings-container">
      {message && (
        <div className={`user-message ${message.type}`}>
          <div className="user-message-content">
            <div className="user-message-icon">
              {message.type === 'success' ? <CheckCircle className="icon-success" /> : <AlertCircle className="icon-error" />}
            </div>
            <div className="user-message-text"><p>{message.text}</p></div>
          </div>
        </div>
      )}

      {!showForm && !editingUser && (
        <div className="user-search-box">
          <div className="user-search-input-wrapper">
            <Search className="user-search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="user-search-input"
            />
          </div>
          <button className="btn-add-user" onClick={() => setShowForm(true)}>
            <Plus className="btn-icon" />
            Invite User
          </button>
        </div>
      )}

      {!showForm && !editingUser && (
        <div className="user-list-container">
          {loading ? (
            <div className="user-loading"><div className="user-spinner" /></div>
          ) : filtered.length > 0 ? (
            <div className="user-table-wrapper">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Role</th>
                    <th className="user-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.ID} className="user-table-row clickable-row" onClick={() => handleEditOpen(u)}>
                      <td>{u.loginname || '—'}</td>
                      <td>{u.firstname || '—'}</td>
                      <td>{u.lastname || '—'}</td>
                      <td>
                        {u.permission_type === 'A'
                          ? <span className="partner-role-badge partner-role-admin">Partner Admin</span>
                          : isVendor(u)
                          ? <span className="partner-role-badge partner-role-vendor" title="A machine integration that calls the Universal API — managed on the Vendors tab">Vendor</span>
                          : u.permission_type === 'U'
                          ? <span className="partner-role-badge partner-role-user">Partner User</span>
                          : '—'}
                      </td>
                      <td className="user-actions-col" onClick={e => e.stopPropagation()}>
                        <div className="user-action-buttons">
                          {/* No mailbox, so nothing to resend to — the credentials were shown once when
                              the vendor was created and are re-issued, not re-sent. */}
                          {!isVendor(u) && (
                          <button
                            className="btn-reinvite-user"
                            disabled={saving}
                            title="Resend invitation"
                            onClick={() => handleResend(u.ID)}
                          >
                            <MailCheck className="action-icon" />
                          </button>
                          )}
                          <button
                            className="btn-delete-user"
                            disabled={saving}
                            title="Remove user"
                            onClick={() => handleRemove(u.ID, u.loginname)}
                          >
                            <Trash2 className="action-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="user-empty-state">
              <User className="empty-state-icon" />
              <h3>No users yet</h3>
              <p>{searchTerm ? 'Try a different search.' : 'Invite your first user to get started.'}</p>
            </div>
          )}
        </div>
      )}

      {editingUser && (
        <div className="user-form-container">
          <div className="user-form-header">
            <h3>Edit User</h3>
            <button className="btn-close-form" onClick={() => setEditingUser(null)}>✕</button>
          </div>
          <div className="user-form-content">
            <div className="user-form">
              <div className="user-form-grid">
                <div className="user-form-group">
                  <label className="user-form-label">Email Address</label>
                  <input
                    type="email"
                    className="user-form-input"
                    value={editingUser.email}
                    disabled
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">First Name *</label>
                  <input
                    type="text"
                    className="user-form-input"
                    value={editingUser.firstname}
                    onChange={e => setEditingUser(p => ({ ...p, firstname: e.target.value }))}
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">Last Name *</label>
                  <input
                    type="text"
                    className="user-form-input"
                    value={editingUser.lastname}
                    onChange={e => setEditingUser(p => ({ ...p, lastname: e.target.value }))}
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">Role *</label>
                  {editingUser.role === 'VENDOR' ? (
                    // PROMOTION IS NOT AN EDIT. Turning an integration into a Partner Admin from a name
                    // field is exactly the shape of change that should never be one click away, and
                    // demoting it would strand a live credential. The row is read here, changed on the
                    // Vendors tab.
                    <>
                      <span className="partner-role-badge partner-role-vendor">Vendor</span>
                      <p className="user-form-hint">
                        A vendor is a machine integration. Its access is the credential it was issued —
                        manage it on the Vendors tab.
                      </p>
                    </>
                  ) : (
                    <select
                      className="user-form-input"
                      value={editingUser.role}
                      onChange={e => setEditingUser(p => ({ ...p, role: e.target.value }))}
                    >
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  )}
                </div>
              </div>
              <div className="user-form-actions">
                <button type="button" className="btn-cancel-form" onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="button" className="btn-save-form" disabled={saving} onClick={handleEditSave}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="user-form-container">
          <div className="user-form-header">
            <h3>Invite User</h3>
            <button className="btn-close-form" onClick={() => setShowForm(false)}>✕</button>
          </div>
          <div className="user-form-content">
            <div className="user-form">
              <div className="user-form-grid">
                <div className="user-form-group">
                  <label className="user-form-label">Email Address *</label>
                  <input
                    type="email"
                    className="user-form-input"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">First Name *</label>
                  <input
                    type="text"
                    className="user-form-input"
                    value={form.firstname}
                    onChange={e => setForm(p => ({ ...p, firstname: e.target.value }))}
                    placeholder="Jane"
                    required
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">Last Name *</label>
                  <input
                    type="text"
                    className="user-form-input"
                    value={form.lastname}
                    onChange={e => setForm(p => ({ ...p, lastname: e.target.value }))}
                    placeholder="Smith"
                    required
                  />
                </div>
                <div className="user-form-group">
                  <label className="user-form-label">Role *</label>
                  <select
                    className="user-form-input"
                    value={form.role}
                    onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                  >
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="user-info-box">
                  <p>A temporary password will be sent to the user's email. They will be required to change it on first login.</p>
                </div>
              </div>
              <div className="user-form-actions">
                <button type="button" className="btn-cancel-form" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="button" className="btn-save-form" disabled={saving} onClick={handleInvite}>
                  <MailCheck className="btn-icon" />
                  {saving ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerUsers;
